import { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Users, Radio, Activity, Crown, Gamepad2, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { api } from "@/lib/api";

function Metric({ icon: Icon, label, value, sub, accent = "text-volt" }) {
    return (
        <div className="bg-surface border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                <Icon className={`w-4 h-4 ${accent}`} /> {label}
            </div>
            <div className="font-heading text-3xl font-bold text-white">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}

export default function AdminBusiness() {
    const [biz, setBiz] = useState(null);
    const [live, setLive] = useState(null);

    const load = () => {
        api.get("/admin/business").then(({ data }) => setBiz(data));
        api.get("/admin/live").then(({ data }) => setLive(data));
    };
    useEffect(() => { load(); const t = setInterval(() => api.get("/admin/live").then(({ data }) => setLive(data)), 8000); return () => clearInterval(t); }, []);

    if (!biz) return <Loader label="Loading business analytics" />;
    const r = biz.revenue, a = biz.audience, e = biz.engagement;

    return (
        <div className="ww-fade-up" data-testid="admin-business">
            <PageHeader title="Business Analytics" subtitle="Real revenue, audience and engagement — live data.">
                <button onClick={load} data-testid="biz-refresh" className="h-9 px-3 rounded-sm border border-border text-sm flex items-center gap-2 hover:bg-white/5">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </PageHeader>

            {/* Revenue */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Metric icon={DollarSign} label="MRR" value={`$${r.mrr.toLocaleString()}`} sub={`$${r.arr.toLocaleString()} ARR`} accent="text-success" />
                <Metric icon={Crown} label="Paying Users" value={r.paying_users} sub={`$${r.pro_price}/mo Pro`} accent="text-success" />
                <Metric icon={TrendingUp} label="Conversion" value={`${r.conversion_pct}%`} sub="Free → Pro" />
                <Metric icon={Users} label="Total Users" value={a.total_users} sub={`${a.verified_pct}% verified`} />
            </div>

            {/* Engagement */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Metric icon={Activity} label="Active 7d" value={e.active_7d} sub={`${e.active_30d} active 30d`} accent="text-warn" />
                <Metric icon={Radio} label="Monitoring Now" value={e.monitoring_now} sub={`${e.live_sessions_total} sessions all-time`} accent="text-alert" />
                <Metric icon={Gamepad2} label="Matches Logged" value={e.matches_total} sub={`${e.avg_matches_per_user} avg / user`} />
                <Metric icon={Users} label="Free Users" value={a.free} sub={`${a.pro} on Pro`} />
            </div>

            {/* Live Now */}
            <div className="bg-surface border border-border rounded-sm overflow-hidden" data-testid="live-now-panel">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                    <span className={`w-2.5 h-2.5 rounded-full ${live?.count ? "bg-success ww-live-dot" : "bg-muted-foreground"}`} />
                    <h3 className="font-heading font-bold">Live Now</h3>
                    <span className="text-xs text-muted-foreground font-mono ml-1">{live?.count || 0} being coached</span>
                </div>
                {live?.count ? (
                    <table className="w-full text-sm">
                        <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left font-semibold px-5 py-2.5">Player</th>
                            <th className="text-left font-semibold px-5 py-2.5">Game</th>
                            <th className="text-left font-semibold px-5 py-2.5 hidden sm:table-cell">Plan</th>
                            <th className="text-right font-semibold px-5 py-2.5">AI</th>
                        </tr></thead>
                        <tbody>
                            {live.live.map((l) => (
                                <tr key={l.user_id} className="border-b border-border/50" data-testid={`live-row-${l.user_id}`}>
                                    <td className="px-5 py-3"><div className="text-white">{l.name}</div><div className="text-xs text-muted-foreground font-mono">{l.email}</div></td>
                                    <td className="px-5 py-3 text-muted-foreground">{l.game}</td>
                                    <td className="px-5 py-3 hidden sm:table-cell"><span className={l.plan === "pro" ? "text-success text-xs uppercase font-mono" : "text-muted-foreground text-xs capitalize"}>{l.plan}</span></td>
                                    <td className="px-5 py-3 text-right"><span className="text-xs font-mono text-success uppercase">{l.ai_status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="px-5 py-10 text-center text-sm text-muted-foreground">No players are being coached right now.</div>
                )}
            </div>
        </div>
    );
}
